import { useEffect, useState } from 'react';

import { safeLocalStorage } from './safeStorage';

const STORAGE_KEY = 'muki-collapsed-document-cards';
// A streaming document carries this id until its metadata arrives (aiAssistant reducer).
const TEMPORARY_DOCUMENT_ID_PREFIX = 'temp-';
export const MAX_COLLAPSED_DOCUMENT_CARDS = 200;

const isTemporaryDocumentId = (documentId: string) =>
	documentId.startsWith(TEMPORARY_DOCUMENT_ID_PREFIX);

// Open is the default, so only collapsed documents are stored.
const readCollapsedIds = (): string[] => {
	try {
		const parsed: unknown = JSON.parse(safeLocalStorage.getItem(STORAGE_KEY) ?? '[]');
		return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
	} catch {
		return [];
	}
};

export const isDocumentCardCollapsed = (documentId: string): boolean =>
	readCollapsedIds().includes(documentId);

export const setDocumentCardCollapsed = (documentId: string, isCollapsed: boolean) => {
	if (isTemporaryDocumentId(documentId)) return;
	const collapsedIds = readCollapsedIds();
	if (collapsedIds.includes(documentId) === isCollapsed) return;
	const otherIds = collapsedIds.filter((id) => id !== documentId);
	const nextIds = isCollapsed
		? [...otherIds, documentId].slice(-MAX_COLLAPSED_DOCUMENT_CARDS)
		: otherIds;
	safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(nextIds));
};

export const useDocumentCardCollapsed = (documentId: string): [boolean, () => void] => {
	const [state, setState] = useState(() => ({
		documentId,
		isCollapsed: isDocumentCardCollapsed(documentId),
	}));

	// The card can get a new id without remounting. The streaming card's temporary id turning
	// into the real one is the same document, so a collapse made before the switch carries over.
	// Any other id change is another document, which starts from its own stored state.
	let current = state;
	if (state.documentId !== documentId) {
		const isSameStreamingDocument =
			isTemporaryDocumentId(state.documentId) && !isTemporaryDocumentId(documentId);
		current = {
			documentId,
			isCollapsed: isSameStreamingDocument ? state.isCollapsed : isDocumentCardCollapsed(documentId),
		};
		setState(current);
	}
	const { isCollapsed } = current;

	useEffect(() => {
		setDocumentCardCollapsed(documentId, isCollapsed);
	}, [documentId, isCollapsed]);

	return [
		isCollapsed,
		() => setState((previous) => ({ ...previous, isCollapsed: !previous.isCollapsed })),
	];
};
